import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Palette, Type, Image, Layout, Sparkles, Save, RefreshCw, 
  Eye, Upload, Wand2, Check, X, Sun, Moon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_THEME = {
  primaryColor: '#3B82F6',
  secondaryColor: '#10B981',
  accentColor: '#F59E0B',
  backgroundColor: '#F9FAFB',
  textColor: '#111827',
  fontFamily: 'Inter',
  borderRadius: '8',
  darkMode: false
};

const FONT_OPTIONS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 
  'Montserrat', 'Source Sans Pro', 'Nunito', 'Raleway', 'Ubuntu'
];

const COLOR_PRESETS = [
  { name: 'Ocean Blue', primary: '#0077B6', secondary: '#00B4D8', accent: '#90E0EF' },
  { name: 'Forest Green', primary: '#2D6A4F', secondary: '#40916C', accent: '#74C69D' },
  { name: 'Sunset Orange', primary: '#E85D04', secondary: '#F48C06', accent: '#FAA307' },
  { name: 'Royal Purple', primary: '#7209B7', secondary: '#9D4EDD', accent: '#C77DFF' },
  { name: 'Cherry Red', primary: '#DC2F02', secondary: '#E85D04', accent: '#F48C06' },
  { name: 'Midnight Dark', primary: '#1E3A5F', secondary: '#3D5A80', accent: '#98C1D9' }
];

export default function AdminDesignTools() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [content, setContent] = useState({
    heroTitle: 'Roadside Help, Anytime',
    heroSubtitle: 'Professional roadside assistance and mobile mechanic services at your fingertips',
    ctaText: 'Get Help Now',
    aboutTitle: 'About Jiffys Assistance',
    aboutText: 'Your trusted partner for roadside emergencies and mobile mechanic services in Fort Wayne, IN.'
  });
  const [logo, setLogo] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  useEffect(() => {
    fetchDesignSettings();
  }, []);

  const fetchDesignSettings = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/design-settings`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      if (response.data.theme) setTheme(response.data.theme);
      if (response.data.content) setContent(response.data.content);
      if (response.data.logo) setLogo(response.data.logo);
    } catch (error) {
      console.error('Failed to load design settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveDesignSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/design-settings`,
        { theme, content, logo },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Design settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const generateAISuggestion = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/design-suggestions`,
        { 
          business_type: 'roadside_assistance',
          current_theme: theme 
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setAiSuggestion(response.data);
      toast.success('AI suggestion generated!');
    } catch (error) {
      toast.error('Failed to generate suggestion');
    } finally {
      setGenerating(false);
    }
  };

  const applyAISuggestion = () => {
    if (aiSuggestion?.theme) {
      setTheme({ ...theme, ...aiSuggestion.theme });
      setAiSuggestion(null);
      toast.success('AI suggestion applied!');
    }
  };

  const applyColorPreset = (preset) => {
    setTheme({
      ...theme,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent
    });
    toast.success(`Applied ${preset.name} theme`);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/upload`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          withCredentials: true 
        }
      );
      setLogo(response.data.url);
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-design-tools">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Design Tools</h1>
            <p className="text-gray-500">Customize your app's appearance with AI assistance</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setPreviewMode(!previewMode)}
            >
              <Eye className="w-4 h-4 mr-2" />
              {previewMode ? 'Edit Mode' : 'Preview'}
            </Button>
            <Button 
              onClick={saveDesignSettings}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* AI Suggestion Card */}
        <Card className="mb-8 border-2 border-dashed border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Design Assistant</h3>
                  <p className="text-sm text-gray-600">
                    Get AI-powered suggestions for colors and styling that match your business
                  </p>
                </div>
              </div>
              <Button 
                onClick={generateAISuggestion}
                disabled={generating}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4 mr-2" />
                )}
                Generate Suggestion
              </Button>
            </div>

            {/* AI Suggestion Preview */}
            {aiSuggestion && (
              <div className="mt-6 p-4 bg-white rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900">AI Suggestion</h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAiSuggestion(null)}>
                      <X className="w-4 h-4 mr-1" /> Dismiss
                    </Button>
                    <Button size="sm" onClick={applyAISuggestion}>
                      <Check className="w-4 h-4 mr-1" /> Apply
                    </Button>
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap">
                  {aiSuggestion.theme?.primaryColor && (
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-white shadow-sm"
                        style={{ backgroundColor: aiSuggestion.theme.primaryColor }}
                      />
                      <span className="text-sm text-gray-600">Primary</span>
                    </div>
                  )}
                  {aiSuggestion.theme?.secondaryColor && (
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-white shadow-sm"
                        style={{ backgroundColor: aiSuggestion.theme.secondaryColor }}
                      />
                      <span className="text-sm text-gray-600">Secondary</span>
                    </div>
                  )}
                  {aiSuggestion.theme?.accentColor && (
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-white shadow-sm"
                        style={{ backgroundColor: aiSuggestion.theme.accentColor }}
                      />
                      <span className="text-sm text-gray-600">Accent</span>
                    </div>
                  )}
                </div>
                {aiSuggestion.reasoning && (
                  <p className="mt-3 text-sm text-gray-600 italic">{aiSuggestion.reasoning}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="colors" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="colors" className="flex items-center gap-2">
              <Palette className="w-4 h-4" /> Colors
            </TabsTrigger>
            <TabsTrigger value="typography" className="flex items-center gap-2">
              <Type className="w-4 h-4" /> Typography
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Image className="w-4 h-4" /> Branding
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Layout className="w-4 h-4" /> Content
            </TabsTrigger>
          </TabsList>

          {/* Colors Tab */}
          <TabsContent value="colors">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Color Palette</CardTitle>
                  <CardDescription>Customize your brand colors</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="primaryColor">Primary Color</Label>
                    <div className="flex gap-3 mt-2">
                      <input
                        type="color"
                        id="primaryColor"
                        value={theme.primaryColor}
                        onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                      />
                      <Input
                        value={theme.primaryColor}
                        onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="secondaryColor">Secondary Color</Label>
                    <div className="flex gap-3 mt-2">
                      <input
                        type="color"
                        id="secondaryColor"
                        value={theme.secondaryColor}
                        onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                      />
                      <Input
                        value={theme.secondaryColor}
                        onChange={(e) => setTheme({ ...theme, secondaryColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="accentColor">Accent Color</Label>
                    <div className="flex gap-3 mt-2">
                      <input
                        type="color"
                        id="accentColor"
                        value={theme.accentColor}
                        onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                      />
                      <Input
                        value={theme.accentColor}
                        onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {theme.darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                      <span className="font-medium">Dark Mode</span>
                    </div>
                    <Button
                      variant={theme.darkMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTheme({ ...theme, darkMode: !theme.darkMode })}
                    >
                      {theme.darkMode ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Color Presets</CardTitle>
                  <CardDescription>Quick apply professional color schemes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => applyColorPreset(preset)}
                        className="p-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors text-left"
                      >
                        <div className="flex gap-2 mb-2">
                          <div 
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: preset.primary }}
                          />
                          <div 
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: preset.secondary }}
                          />
                          <div 
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: preset.accent }}
                          />
                        </div>
                        <p className="font-medium text-sm text-gray-900">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Typography Tab */}
          <TabsContent value="typography">
            <Card>
              <CardHeader>
                <CardTitle>Typography Settings</CardTitle>
                <CardDescription>Choose fonts and text styling</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="fontFamily">Font Family</Label>
                  <select
                    id="fontFamily"
                    value={theme.fontFamily}
                    onChange={(e) => setTheme({ ...theme, fontFamily: e.target.value })}
                    className="w-full mt-2 p-3 border rounded-lg bg-white"
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="borderRadius">Border Radius (px)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <input
                      type="range"
                      id="borderRadius"
                      min="0"
                      max="24"
                      value={theme.borderRadius}
                      onChange={(e) => setTheme({ ...theme, borderRadius: e.target.value })}
                      className="flex-1"
                    />
                    <span className="w-12 text-center font-medium">{theme.borderRadius}px</span>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-6 bg-gray-100 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">Preview</h4>
                  <div 
                    className="p-4 bg-white shadow-sm"
                    style={{ 
                      fontFamily: theme.fontFamily,
                      borderRadius: `${theme.borderRadius}px`
                    }}
                  >
                    <h3 
                      className="text-xl font-bold mb-2"
                      style={{ color: theme.primaryColor }}
                    >
                      Sample Heading
                    </h3>
                    <p className="text-gray-600 mb-4">
                      This is how your text will look with the selected font and styling options.
                    </p>
                    <button
                      className="px-4 py-2 text-white font-medium"
                      style={{ 
                        backgroundColor: theme.primaryColor,
                        borderRadius: `${theme.borderRadius}px`
                      }}
                    >
                      Sample Button
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle>Logo & Branding</CardTitle>
                <CardDescription>Upload your logo and brand assets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Logo</Label>
                  <div className="mt-2 flex items-center gap-6">
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
                      {logo ? (
                        <img src={logo} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <Image className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button 
                        variant="outline"
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </Button>
                      <p className="text-sm text-gray-500 mt-2">
                        Recommended: 512x512px, PNG or SVG
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <CardTitle>Landing Page Content</CardTitle>
                <CardDescription>Edit text displayed on your landing page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="heroTitle">Hero Title</Label>
                  <Input
                    id="heroTitle"
                    value={content.heroTitle}
                    onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="heroSubtitle">Hero Subtitle</Label>
                  <Textarea
                    id="heroSubtitle"
                    value={content.heroSubtitle}
                    onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                    className="mt-2"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="ctaText">Call-to-Action Button Text</Label>
                  <Input
                    id="ctaText"
                    value={content.ctaText}
                    onChange={(e) => setContent({ ...content, ctaText: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="aboutTitle">About Section Title</Label>
                  <Input
                    id="aboutTitle"
                    value={content.aboutTitle}
                    onChange={(e) => setContent({ ...content, aboutTitle: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="aboutText">About Section Text</Label>
                  <Textarea
                    id="aboutText"
                    value={content.aboutText}
                    onChange={(e) => setContent({ ...content, aboutText: e.target.value })}
                    className="mt-2"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
